/**
 * =============================================================================
 * cart.js
 * =============================================================================
 * Collection of useful utilities for performing actions on the cart / minicart
 * =============================================================================
 */

var Cart = {
  selectors: { 
    line_item: "[data-tile-line-item]",
    line_item_quantity: "[data-cart-item-quantity]",
    line_item_remove: "[data-cart-item-remove]",
    line_item_subscription_toggle: "[data-cart-subscription-toggle]",

    cart_item_count: "[data-cart-item-count]", 
    cart_item_count_attribute: "data-cart-item-count",

    minicart: "cart-offcanvas",

    variant_quick_add: "[data-quick-add]",
    checkout_link: 'a[href="/checkout"], a[href^="/checkout?"]'
  },

  minicart: null,
  rivoMemberAttributeSynced: false,

  /**
   * Document ready
   */
  onReady: function(){
    this.syncRivoMemberAttribute();

    // Handle remove / quick add 'click'
    document.body.addEventListener('click', e => {
      if(e.target.closest(this.selectors.line_item_remove)){
        this.onLineItemRemove(e);
      };

      if(e.target.closest(this.selectors.variant_quick_add)){
        this.onVariantQuickAdd(e);
      };

      if(e.target.closest(".rebuy-button")){
        this.fetchAndRenderMinicart();
      }

      if(e.target.closest(this.selectors.checkout_link)){
        this.onCheckoutLinkClick(e);
      };
    });

    // Handle quantity select 'change'
    document.body.addEventListener('change', e => {
      if(e.target.closest(this.selectors.line_item_quantity)){
        this.onLineItemQuantityChange(e);
      };

      if(e.target.closest(this.selectors.line_item_subscription_toggle)){
        this.onLineItemSubscriptionToggleChange(e);
      };
    });

    this.minicart = new bootstrap.Offcanvas(document.getElementById(this.selectors.minicart));
  },

  /**
   * Keep the Rivo membership order attribute in sync before direct checkout links
   * navigate away from the theme.
   *
   * @param {Event} e
   */
  onCheckoutLinkClick: async function(e) {
    if(e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey){
      return;
    }

    const checkoutLink = e.target.closest(this.selectors.checkout_link);
    if(!checkoutLink || checkoutLink.target === '_blank'){
      return;
    }

    e.preventDefault();
    await this.syncRivoMemberAttribute(true);
    window.location.href = checkoutLink.href;
  },

  /**
   * Update the cart/order attribute used by Shopify notifications.
   *
   * @param {boolean} force
   * @returns {Promise}
   */
  syncRivoMemberAttribute: async function(force = false) {
    const rivoMemberAttribute = window.rivoMemberAttributeEnabled && window.rivoMember ? 'true' : 'false';
    const currentRivoMemberAttribute = this.getCurrentRivoMemberAttribute();

    if(!force && currentRivoMemberAttribute === null){
      return;
    }

    if(
      !force
      && currentRivoMemberAttribute === undefined
      && rivoMemberAttribute === 'false'
    ){
      this.rivoMemberAttributeSynced = true;
      return;
    }

    if(
      !force
      && this.rivoMemberAttributeSynced
      && currentRivoMemberAttribute === rivoMemberAttribute
    ){
      return;
    }

    if(!force && currentRivoMemberAttribute === rivoMemberAttribute){
      this.rivoMemberAttributeSynced = true;
      return;
    }

    const config = MP.Utilities.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.body = JSON.stringify({
      attributes: {
        rivo_member: rivoMemberAttribute
      }
    });

    try {
      const response = await fetch(`${routes.cart_update_url}`, config);
      const cart = await response.json();
      this.rivoMemberAttributeSynced = true;
      return cart;
    } catch(e) {
      console.warn('[C] Failed to sync Rivo member cart attribute', e);
    }
  },

  /**
   * Get the cart's currently rendered Rivo member attribute value.
   *
   * @returns {string|null|undefined}
   */
  getCurrentRivoMemberAttribute: function() {
    const cartJson = document.querySelector('#cart-json') || document.querySelector('#minicart-json');
    if(!cartJson){
      return null;
    }

    try {
      const cart = JSON.parse(cartJson.innerText);
      return cart.attributes?.rivo_member;
    } catch(e) {
      return;
    }
  },

	/**
   * Add a variant using the data-quick-add attribute as the variant ID
   * 
   * @param {Event} e 
   */
  onVariantQuickAdd: function(e) {
    let variantId = e.target.closest(this.selectors.variant_quick_add).dataset.quickAdd;
    this.add({ id: variantId, quantity: 1 });
  },

  /**
   * Remove a line item from the cart
   * 
   * @param {Event} e 
   */
  onLineItemRemove: function(e) {
    e.preventDefault();
    let lineItem = e.target.closest(this.selectors.line_item);
    let lineItemKey = lineItem.dataset.tileLineItem;
    let payload = { id: lineItemKey, quantity: 0 };
    this.change(payload);
  },

  /**
   * Update quantity of a line item in the cart
   * 
   * @param {Event} e 
   */
  onLineItemQuantityChange: function(e) {
    let lineItem = e.target.closest(this.selectors.line_item);
    let lineItemKey = lineItem.dataset.tileLineItem;
    let payload = { id: lineItemKey, quantity: e.target.value };
    this.change(payload);
  },

  /**
   * Toggle line item between one-time purchase and subscription
   *
   * @param {Event} e
   */
  onLineItemSubscriptionToggleChange: function(e) {
    let toggle = e.target.closest(this.selectors.line_item_subscription_toggle);
    if(!toggle) return;

    let lineItem = toggle.closest(this.selectors.line_item);
    if(!lineItem) return;

    let lineItemKey = lineItem.dataset.lineItemKey || lineItem.dataset.tileLineItem;
    let lineItemVariantId = lineItem.dataset.lineItemVariantId || '';
    let lineItemQuantity = parseInt(lineItem.dataset.lineItemQuantity || 1, 10);
    if(Number.isNaN(lineItemQuantity) || lineItemQuantity < 1){
      lineItemQuantity = 1;
    }

    let currentSellingPlanId = lineItem.dataset.currentSellingPlanId || '';
    let defaultSellingPlanId = lineItem.dataset.defaultRechargeSellingPlanId || '';
    let subscriptionEnabled = toggle.checked;
    let targetSellingPlanId = subscriptionEnabled ? defaultSellingPlanId : '';

    // Prevent duplicate submissions while cart/update is in progress
    toggle.disabled = true;

    let payload = {
      id: lineItemKey,
      quantity: lineItemQuantity
    };

    if(subscriptionEnabled){
      if(defaultSellingPlanId === ''){
        toggle.checked = false;
        toggle.disabled = false;
        this.handleErrorMessage('No subscription plan is available for this item.');
        return;
      }
      payload.selling_plan = targetSellingPlanId;
    } else {
      payload.selling_plan = '';
    }

    this.change(payload)
      .then(response => {
        if(response?.status){
          return this.fallbackLineItemPurchaseTypeChange({
            lineItemKey: lineItemKey,
            variantId: lineItemVariantId,
            quantity: lineItemQuantity,
            targetSellingPlanId: targetSellingPlanId
          });
        }
        return response;
      })
      .catch(() => this.fallbackLineItemPurchaseTypeChange({
        lineItemKey: lineItemKey,
        variantId: lineItemVariantId,
        quantity: lineItemQuantity,
        targetSellingPlanId: targetSellingPlanId
      }))
      .catch(() => {
        toggle.checked = currentSellingPlanId !== '';
      })
      .finally(() => {
        toggle.disabled = false;
      });
  },

  /**
   * Fallback path for switching purchase type:
   * add the target mode as a new line, then remove original line.
   *
   * @param {Object} data
   * @returns {Promise}
   */
  fallbackLineItemPurchaseTypeChange: function(data) {
    if(!data.variantId){
      return Promise.reject(new Error('Missing variant ID for fallback.'));
    }

    let addPayload = {
      id: data.variantId,
      quantity: data.quantity
    };

    if(data.targetSellingPlanId){
      addPayload.selling_plan = data.targetSellingPlanId;
    }

    // Keep minicart from opening during the two-step fallback operation.
    window.disableNextMinicartOpen = true;
    return this.add(addPayload)
      .catch(() => {
        throw new Error('Unable to add replacement line item.');
      })
      .then(addResponse => {
        if(addResponse?.status){
          throw new Error(addResponse.description || 'Unable to add replacement line item.');
        }

        window.disableNextMinicartOpen = true;
        return this.change({ id: data.lineItemKey, quantity: 0 });
      })
      .then(removeResponse => {
        if(removeResponse?.status){
          throw new Error(removeResponse.description || 'Unable to remove original line item.');
        }
        return removeResponse;
      });
  },

  /**
   * Fetch the minicart HTML and update
   */
  fetchAndRenderMinicart: function(show = true) {
    // Get the template-namespaced ID of the cart-items section to dynamically render
    
    const sectionId = document.querySelector(".cart-offcanvas").id.replace("shopify-section-", "");
    const fetchPromise = fetch(`${location.pathname}?section_id=${sectionId}`)
      .then(response => response.text())
      .then(response => {
        document.querySelector('.cart-offcanvas').outerHTML = response;
        const parsedHtml = new DOMParser().parseFromString(response, 'text/html');
        const numItems = parseInt(parsedHtml.querySelector("#cart-offcanvas-content").dataset.cartItemsCount);
        let itemCounts = document.querySelectorAll("[data-cart-item-count]");
        itemCounts.forEach(e => {
          e.dataset.cartItemCount = numItems;
          e.innerHTML = numItems; 
        });
        mp_lazyload_setup();

        let cartJSON = JSON.parse(parsedHtml.querySelector("#minicart-json").innerText)
        var event = new CustomEvent("minicart:updated", { detail: {
          cart: cartJSON
        }});
        document.dispatchEvent(event);

        if(show){
          this.minicart.show();
        }
      })
      .finally(() => document.querySelectorAll('.cart-loading').forEach(e => e.classList.add('d-none')));
      
  },

  fetchAndRenderCart: function() {
    // Get the template-namespaced ID of the cart-items section to dynamically render
    const sectionId = document.querySelector(".cart").id.replace("shopify-section-", "");
    const fetchPromise = fetch(`${location.pathname}?section_id=${sectionId}`)
      .then(response => response.text())
      .then(response => {
        const parsedHtml = new DOMParser().parseFromString(response, 'text/html');
        const numItems = parseInt(parsedHtml.querySelector("#cart-container").dataset.cartItemsCount);
        let itemCounts = document.querySelectorAll("[data-cart-item-count]");
        itemCounts.forEach(e => {
          e.dataset.cartItemCount = numItems;
          e.innerHTML = numItems; 
        });
        if(numItems > 0){
          document.querySelectorAll("[data-live-region='cart']").forEach(element => {
            let refreshedElement = parsedHtml.getElementById(element.id)
            if(refreshedElement){
              element.innerHTML = refreshedElement.innerHTML;
              console.debug("[C] Refreshed live region", refreshedElement.id);
            }
          });
  
          mp_lazyload_setup();
          
          try {
            window.Klarna.OnsiteMessaging.refresh();
          } catch(e) {
            console.warn("[C] Failed to refresh Klarna messaging");
          }
        } else {
          document.querySelector("#cart-container").outerHTML = parsedHtml.querySelector("#cart-container").outerHTML;
          window.location.reload();
        }
        let cartJSON = JSON.parse(parsedHtml.querySelector("#cart-json").innerText)
        var event = new CustomEvent("cart:changed", { detail: {
          cart: cartJSON
        }});
        document.dispatchEvent(event);
      })
      .finally(() => document.querySelectorAll('.cart-loading').forEach(e => e.classList.add('d-none')));
  },

  /**
   * Update items already in the cart
   * 
   * @param {Object} updates 
   * @returns {Promise}
   */
  change: function(updates) {
    document.querySelectorAll('.cart-loading').forEach(e => e.classList.remove('d-none'));

    // Setup the fetch config
    const config = MP.Utilities.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.body = JSON.stringify(updates);

    // Parse the response to JSON for any other callbacks
    const fetchPromise = fetch(`${routes.cart_change_url}`, config).then(response => response.json());


    // Handle the promise
    fetchPromise
      .then(response => this.handleCartUpdated(response, window.cartStrings.updated))
      .catch(error => {
        this.handleErrorMessage(error.message);
        document.querySelectorAll('.cart-loading').forEach(e => e.classList.add('d-none'));
      });

    // Pass the promise back to the caller to do any caller-specific handling
    return fetchPromise;
  },

  /**
   * Add item(s) to the cart
   * 
   * @param {Object} data 
   * @returns {Promise}
   */
  add: function(data) {
    document.querySelectorAll('.cart-loading').forEach(e => e.classList.remove('d-none'));
    
    // Setup the fetch request
    const config = MP.Utilities.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';

    // Determine the payload
    if(data instanceof FormData){ // Product form is passed as a FormData object, *not* JSON
      config.body = data;
      delete config.headers['Content-Type'];
    } else if (data instanceof Array){ // Simple array of variant objects - i.e [{id: XXX, quantity: 1, properties: {}}]
      config.body = JSON.stringify({ items: data })
    } else { // Single variant object, so we wrap in an array
      config.body = JSON.stringify({ items: [data] })
    }

    // Parse the response to JSON for any other callbacks
    const fetchPromise = fetch(`${routes.cart_add_url}`, config).then(response => response.json());

    // Handle the promise
    fetchPromise.then(response => {
      if(response.status) {
        this.handleErrorMessage(response.description);
      } else {
        this.handleCartUpdated(response, window.cartStrings.added);
      }
    });

    // Pass the promise back to the caller to do any caller-specific handling
    return fetchPromise;
  },

  /**
   * Handle a cart add response
   * 
   * @param {Object} response The JSON response
   * @returns {Promise} The Promise from renderCarts()
   */
  handleCartUpdated: function(response, successMessage) {
    if (response.status) {
      this.handleErrorMessage(response.description);
      document.querySelectorAll('.cart-loading').forEach(e => e.classList.add('d-none'));
      return;
    }

    return this.renderCarts();
  },

  /**
   * Display error on failure
   */
  handleErrorMessage: function(errorMessage) {
    let message = "";
    if (typeof errorMessage === 'object'){
      for (const [key, value] of Object.entries(errorMessage)) {
        message = `${message} ${key} ${value}`;
      }
    } else {
      message = errorMessage;
    }
    MP.Utilities.toast("warning", message, "fal fa-exclamation-triangle");
  },

  /**
   * Render the current cart / minicart
   * 
   * @returns {Promise}
   */
  renderCarts: function() {
    let openMinicart = true;
    if(window.disableNextMinicartOpen){
      openMinicart = false;
      delete window.disableNextMinicartOpen;
    }

    // Actually get the cart 
    if(MP.Utilities.isCartPage()){
      this.fetchAndRenderCart();
      this.fetchAndRenderMinicart(false);
    } else {
      this.fetchAndRenderMinicart(openMinicart);
    }
  }
}

export default Cart;